import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Tab, TextField, ThemeProvider, Tooltip, Typography } from "@mui/material";
import { TabContext, TabList } from "@mui/lab";
import { useCallback, useEffect, useMemo, useRef, useState, type SyntheticEvent } from "react";
import { useDropzone } from "react-dropzone";
import { DataGrid, type GridColDef, type GridSortModel, type GridFilterModel } from "@mui/x-data-grid";
import { format } from "date-fns";
import { DeleteRegular, EditRegular } from "@fluentui/react-icons";
import { Icon } from "@fluentui/react/lib/Icon";
import { ConfirmationDialog, ErrorMessage } from "components/ui";
import { muiTheme } from "system/styles/muiTheme";
import LoadingPage from "./system/LoadingPage";
import useAskAidenApi, { isAskAidenApiError, getAskAidenErrorMessage } from "system/hooks/use-api-askAiden";
import { useMsal } from "@azure/msal-react";
import { getAccountEmail } from "system/utils/getAccountEmail";
import { fluentUiDarkTheme } from "system/styles/fluent-ui-classes";

interface IUrlManagementRow {
  id: number;
  // False when the API response didn't include a real id and this row's id is a
  // fabricated placeholder - editing/deleting it would act on the wrong record (or
  // nothing), so the UI must disable those actions whenever this is false.
  hasValidId: boolean;
  url: string;
  description: string;
  dateCreated: string;
  dateModified: string;
  modifiedBy: string;
  createdBy: string;
}

interface IDocumentManagementRow {
  id: number;
  // See IUrlManagementRow.hasValidId.
  hasValidId: boolean;
  name: string;
  description: string;
  type: string;
  dateCreated: string;
  dateModified: string;
  modifiedBy: string;
  createdBy: string;
}

type ModalMode = "add" | "edit";

const URL_MAX_LENGTH = 2048;
const DESCRIPTION_MAX_LENGTH = 1000;
const FILE_NAME_MAX_LENGTH = 255;
// Hardcoded in code rather than read from an env var - change this constant to adjust the limit.
// Must stay in sync with MAX_UPLOAD_FILE_SIZE_MB in the Ask Aiden API (API/api_app/__init__.py).
const MAX_UPLOAD_FILE_SIZE_MB = 100;
const MAX_UPLOAD_FILE_SIZE_BYTES = MAX_UPLOAD_FILE_SIZE_MB * 1024 * 1024;

const isValidHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const splitFileName = (fileName: string): { base: string; extension: string } => {
  const lastDot = fileName.lastIndexOf(".");
  if (lastDot <= 0) return { base: fileName, extension: "" };
  return { base: fileName.slice(0, lastDot), extension: fileName.slice(lastDot) };
};

// Canonical "Type" options - restricted to exactly the file types the Azure AI Search blob
// indexer we use can natively extract content from (no OCR/media cognitive skill is
// configured, so images/audio/video/other archive formats wouldn't be searchable even if
// uploaded). Drives three things from one list: the Type column's filter dropdown (`key`
// is what it sends to /admin/documents - see that endpoint's DOCUMENT_TYPE_FILTER_MIME_TYPES,
// which must stay in sync), the MIME-to-label lookup used to display the column, and the
// set of extensions allowed on upload (both the dropzone validator here and the server-side
// check in ALLOWED_UPLOAD_EXTENSIONS, API/api_app/__init__.py).
const DOCUMENT_TYPE_OPTIONS: { key: string; label: string; mimeTypes: string[]; extensions: string[] }[] = [
  { key: "pdf", label: "PDF Document", mimeTypes: ["application/pdf"], extensions: [".pdf"] },
  {
    key: "word",
    label: "Word Document",
    mimeTypes: ["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
    extensions: [".doc", ".docx"],
  },
  {
    key: "excel",
    label: "Excel Spreadsheet",
    mimeTypes: ["application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
    extensions: [".xls", ".xlsx"],
  },
  {
    key: "powerpoint",
    label: "PowerPoint Presentation",
    mimeTypes: ["application/vnd.ms-powerpoint", "application/vnd.openxmlformats-officedocument.presentationml.presentation"],
    extensions: [".ppt", ".pptx"],
  },
  { key: "text", label: "Text File", mimeTypes: ["text/plain"], extensions: [".txt"] },
  { key: "csv", label: "CSV File", mimeTypes: ["text/csv"], extensions: [".csv"] },
  { key: "html", label: "HTML File", mimeTypes: ["text/html"], extensions: [".html", ".htm"] },
  { key: "xml", label: "XML File", mimeTypes: ["application/xml", "text/xml"], extensions: [".xml"] },
  { key: "json", label: "JSON File", mimeTypes: ["application/json"], extensions: [".json"] },
  { key: "rtf", label: "RTF File", mimeTypes: ["application/rtf"], extensions: [".rtf"] },
  { key: "markdown", label: "Markdown File", mimeTypes: ["text/markdown"], extensions: [".md", ".markdown"] },
  { key: "zip", label: "ZIP Archive", mimeTypes: ["application/zip"], extensions: [".zip"] },
  {
    key: "email",
    label: "Email Message",
    mimeTypes: ["application/vnd.ms-outlook", "message/rfc822"],
    extensions: [".msg", ".eml"],
  },
  {
    key: "opendocument",
    label: "OpenDocument File",
    mimeTypes: [
      "application/vnd.oasis.opendocument.text",
      "application/vnd.oasis.opendocument.spreadsheet",
      "application/vnd.oasis.opendocument.presentation",
    ],
    extensions: [".odt", ".ods", ".odp"],
  },
];

const MIME_TYPE_LABELS: Record<string, string> = DOCUMENT_TYPE_OPTIONS.reduce<Record<string, string>>((acc, option) => {
  option.mimeTypes.forEach((mimeType) => {
    acc[mimeType] = option.label;
  });
  return acc;
}, {});

const ALLOWED_UPLOAD_EXTENSIONS = DOCUMENT_TYPE_OPTIONS.flatMap((option) => option.extensions);
const ALLOWED_UPLOAD_TYPES_NOTE = `Supported file types: ${ALLOWED_UPLOAD_EXTENSIONS.join(", ")}.`;

const getExtensionLabel = (fileName: string): string => {
  const { extension } = splitFileName(fileName);
  return extension ? `${extension.slice(1).toUpperCase()} File` : "";
};

const getReadableFileType = (type: string, fileName: string): string => {
  if (!type) return fileName ? getExtensionLabel(fileName) || "Unknown" : "Unknown";
  if (type === "Folder") return "Folder";

  const knownLabel = MIME_TYPE_LABELS[type.toLowerCase()];
  if (knownLabel) return knownLabel;

  // Unrecognized MIME type - fall back to the file's extension rather than showing the raw MIME string.
  const extensionLabel = fileName ? getExtensionLabel(fileName) : "";
  if (extensionLabel) return extensionLabel;

  const subtype = type.split("/")[1];
  return subtype ? subtype.toUpperCase() : type;
};

const isAllowedUploadFile = (fileName: string): boolean => {
  const { extension } = splitFileName(fileName);
  return ALLOWED_UPLOAD_EXTENSIONS.includes(extension.toLowerCase());
};

const AskAidenDocumentsPage = () => {
  const { accounts } = useMsal();
  const [activeTab, setActiveTab] = useState("url-management");
  const [hasLoadedUrlsOnce, setHasLoadedUrlsOnce] = useState(false);
  const [hasLoadedDocumentsOnce, setHasLoadedDocumentsOnce] = useState(false);
  const isLoading = !hasLoadedUrlsOnce || !hasLoadedDocumentsOnce;
  const [isUrlsLoading, setIsUrlsLoading] = useState(false);
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [urlRows, setUrlRows] = useState<IUrlManagementRow[]>([]);
  const [urlTotalCount, setUrlTotalCount] = useState(0);
  const [urlPaginationModel, setUrlPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [urlSortModel, setUrlSortModel] = useState<GridSortModel>([{ field: "dateModified", sort: "desc" }]);
  const [urlFilterModel, setUrlFilterModel] = useState<GridFilterModel>({ items: [] });
  // Bumped to force a URL refetch (e.g. after save/delete) even when the pagination model
  // doesn't otherwise change - see documentReloadToken below for why this is needed.
  const [urlReloadToken, setUrlReloadToken] = useState(0);
  const [documentRows, setDocumentRows] = useState<IDocumentManagementRow[]>([]);
  const [documentTotalCount, setDocumentTotalCount] = useState(0);
  const [documentPaginationModel, setDocumentPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [documentSortModel, setDocumentSortModel] = useState<GridSortModel>([{ field: "dateCreated", sort: "desc" }]);
  const [documentFilterModel, setDocumentFilterModel] = useState<GridFilterModel>({ items: [] });
  // Bumped to force a document refetch (e.g. after save/delete) even when the pagination
  // model doesn't otherwise change - guarantees a single request rather than racing one
  // triggered directly against one triggered by a pagination-model state change.
  const [documentReloadToken, setDocumentReloadToken] = useState(0);
  // Guards Save/Upload against a second click while the request (URL crawl, or an upload
  // up to 100 MB) is still in flight - without it, a second click creates a duplicate.
  const [isSaving, setIsSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("add");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<{ title: string; message: string; visible: boolean }>({
    title: "",
    message: "",
    visible: false,
  });
  const [urlForm, setUrlForm] = useState({ url: "", description: "" });
  const [documentForm, setDocumentForm] = useState({ name: "", description: "", type: "", modifiedBy: "", createdBy: "", fileName: "", file: null as File | null });
  const [fileError, setFileError] = useState("");
  const api = useAskAidenApi();
  const userEmail = getAccountEmail(accounts[0]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0] ?? null;
      setFileError("");
      setDocumentForm((current) => ({
        ...current,
        fileName: file?.name ?? "",
        file,
      }));
    },
    onDropRejected: (fileRejections) => {
      const isTypeError = fileRejections[0]?.errors.some((error) => error.code === "file-invalid-type");
      setFileError(
        isTypeError
          ? `Unsupported file type. ${ALLOWED_UPLOAD_TYPES_NOTE}`
          : `File exceeds the maximum allowed size of ${MAX_UPLOAD_FILE_SIZE_MB} MB.`
      );
    },
    multiple: false,
    maxSize: MAX_UPLOAD_FILE_SIZE_BYTES,
    validator: (file) =>
      isAllowedUploadFile(file.name) ? null : { code: "file-invalid-type", message: "Unsupported file type" },
  });

  // Guards against an older in-flight request's response landing after a newer one - see
  // documentRequestIdRef below for why this is needed.
  const urlRequestIdRef = useRef(0);

  // URL listing is server-side paginated/searched/sorted/filtered - this refetches whenever
  // the page, page size, debounced search text, sort model, or filter model changes.
  const loadUrls = useCallback(async () => {
    const requestId = ++urlRequestIdRef.current;
    setIsUrlsLoading(true);

    try {
      const sortItem = urlSortModel[0];
      const { items, totalCount } = await api.documents.urlManagement({
        page: urlPaginationModel.page,
        pageSize: urlPaginationModel.pageSize,
        search: debouncedSearchText,
        sortBy: sortItem?.field,
        sortDir: sortItem?.sort ?? undefined,
        filters: urlFilterModel.items
          .filter((item) => item.value !== undefined && item.value !== "")
          .map((item) => ({ field: item.field, operator: item.operator, value: item.value })),
      });

      if (requestId !== urlRequestIdRef.current) {
        return;
      }

      setUrlRows(
        items.map((item: any, index: number) => {
          const realId = item.id ?? item.Id;
          if (realId === undefined || realId === null) {
            console.error("AskAiden URL row is missing an id - edit/delete will be disabled for it", item);
          }
          return {
            // Negative, index-derived placeholder can't collide with a real (positive) backend id.
            id: realId ?? -(index + 1),
            hasValidId: realId !== undefined && realId !== null,
            url: item.url ?? item.Url ?? "",
            description: item.description ?? item.Description ?? "",
            dateCreated: item.dateCreated ?? item["Date Created"] ?? "",
            dateModified: item.dateModified ?? item["Date Modified"] ?? "",
            modifiedBy: item.modifiedBy ?? item["Modified By"] ?? "",
            createdBy: item.createdBy ?? item["Created By"] ?? "",
          };
        })
      );
      setUrlTotalCount(totalCount);
    } catch (error) {
      if (requestId !== urlRequestIdRef.current) {
        return;
      }
      console.error("URL API Failed", error);
      setUrlRows([]);
      setUrlTotalCount(0);
      setErrorMessage({ title: "Load Failed", message: (error as Error)?.message || "Failed to load URLs", visible: true });
    } finally {
      if (requestId === urlRequestIdRef.current) {
        setIsUrlsLoading(false);
        setHasLoadedUrlsOnce(true);
      }
    }
  }, [api, urlPaginationModel, debouncedSearchText, urlSortModel, urlFilterModel, urlReloadToken]);

  // Reset to the first page and force a refetch - used after a URL is created, updated, or
  // deleted, so the (possibly reordered/resized) result set is reflected even when the user
  // was already viewing page 0.
  const reloadUrlsFromStart = useCallback(() => {
    setUrlPaginationModel((current) => (current.page === 0 ? current : { ...current, page: 0 }));
    setUrlReloadToken((token) => token + 1);
  }, []);

  // Guards against an older in-flight request's response landing after a newer one - e.g.
  // changing the search while on page 3 fires a page-3 request, then the page-reset effect
  // below fires a page-0 request; if the page-3 response arrives last it must not overwrite
  // the page-0 rows that belong on screen.
  const documentRequestIdRef = useRef(0);

  // Document listing is server-side paginated/searched/sorted/filtered - this refetches
  // whenever the page, page size, debounced search text, sort model, or filter model changes.
  const loadDocuments = useCallback(async () => {
    const requestId = ++documentRequestIdRef.current;
    setIsDocumentsLoading(true);

    try {
      const sortItem = documentSortModel[0];
      const { items, totalCount } = await api.documents.documentManagement({
        page: documentPaginationModel.page,
        pageSize: documentPaginationModel.pageSize,
        search: debouncedSearchText,
        sortBy: sortItem?.field,
        sortDir: sortItem?.sort ?? undefined,
        filters: documentFilterModel.items
          .filter((item) => item.value !== undefined && item.value !== "")
          .map((item) => ({ field: item.field, operator: item.operator, value: item.value })),
      });

      if (requestId !== documentRequestIdRef.current) {
        return;
      }

      setDocumentRows(
        items.map((item: any, index: number) => {
          const realId = item.id;
          if (realId === undefined || realId === null) {
            console.error("AskAiden document row is missing an id - edit/delete will be disabled for it", item);
          }
          return {
            // Negative, index-derived placeholder can't collide with a real (positive) backend id.
            id: realId ?? -(index + 1),
            hasValidId: realId !== undefined && realId !== null,
            name: item.name ?? "",
            description: item.description ?? "",
            type: item.type ?? "",
            dateCreated: item.dateCreated ?? "",
            dateModified: item.dateModified ?? "",
            modifiedBy: item.modifiedBy ?? "",
            createdBy: item.createdBy ?? "",
          };
        })
      );
      setDocumentTotalCount(totalCount);
    } catch (error) {
      if (requestId !== documentRequestIdRef.current) {
        return;
      }
      console.error("Document API Failed", error);
      setDocumentRows([]);
      setDocumentTotalCount(0);
      setErrorMessage({ title: "Load Failed", message: (error as Error)?.message || "Failed to load documents", visible: true });
    } finally {
      if (requestId === documentRequestIdRef.current) {
        setIsDocumentsLoading(false);
        setHasLoadedDocumentsOnce(true);
      }
    }
  }, [api, documentPaginationModel, debouncedSearchText, documentSortModel, documentFilterModel, documentReloadToken]);

  // Reset to the first page and force a refetch - used after a document is created,
  // updated, or deleted, so the (possibly reordered/resized) result set is reflected
  // even when the user was already viewing page 0.
  const reloadDocumentsFromStart = useCallback(() => {
    setDocumentPaginationModel((current) => (current.page === 0 ? current : { ...current, page: 0 }));
    setDocumentReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    loadUrls();
  }, [loadUrls]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Debounce the shared search box before it drives a server-side document search request.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearchText(searchText.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchText]);

  // A new search, sort, or filter should start back on the first page of results.
  useEffect(() => {
    setDocumentPaginationModel((current) => (current.page === 0 ? current : { ...current, page: 0 }));
  }, [debouncedSearchText, documentSortModel, documentFilterModel]);

  useEffect(() => {
    setUrlPaginationModel((current) => (current.page === 0 ? current : { ...current, page: 0 }));
  }, [debouncedSearchText, urlSortModel, urlFilterModel]);

  const refreshHandler = useCallback(() => {
    loadUrls();
    loadDocuments();
  }, [loadUrls, loadDocuments]);

  const handleTabChange = (_event: SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const handleOpenAddModal = () => {
    setModalMode("add");
    setEditingId(null);
    setFileError("");
    if (activeTab === "url-management") {
      setUrlForm({ url: "", description: "" });
    } else {
      setDocumentForm({ name: "", description: "", type: "", modifiedBy: "", createdBy: userEmail, fileName: "", file: null });
    }
    setModalOpen(true);
  };

  const handleOpenEditModal = (row: IUrlManagementRow | IDocumentManagementRow) => {
    setModalMode("edit");
    setEditingId(row.id);
    setFileError("");
    if (activeTab === "url-management") {
      const urlRow = row as IUrlManagementRow;
      setUrlForm({
        url: urlRow.url,
        description: urlRow.description,
      });
    } else {
      const documentRow = row as IDocumentManagementRow;
      const { base } = splitFileName(documentRow.name || "");
      setDocumentForm({
        name: base,
        description: documentRow.description,
        type: documentRow.type,
        modifiedBy: documentRow.modifiedBy,
        createdBy: documentRow.createdBy || userEmail,
        fileName: documentRow.name || "",
        file: null,
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setFileError("");
  };

  const handleSave = async () => {
    // Blocks a second click while the request (URL crawl, or an upload up to 100 MB) is
    // still in flight - the Save/Add button is also disabled below for the same reason.
    if (isSaving) {
      return;
    }
    setIsSaving(true);

    try {
      // On failure, leave the modal open (with the entered values intact) instead of
      // closing it, so the user can fix the input and resubmit without reopening it.
      if (activeTab === "url-management") {
        try {
          const isCreate = !(modalMode === "edit" && editingId);
          // CreatedBy/ModifiedBy aren't sent here - the server sets them from the validated
          // admin's token, not from client input (see use-api-askAiden.ts createUrl/updateUrl).
          const payload = {
            url: urlForm.url.trim(),
            description: urlForm.description.trim(),
          };

          const result = isCreate ? await api.documents.createUrl(payload) : await api.documents.updateUrl(editingId!, payload);

          if (isAskAidenApiError(result)) {
            // Creating a URL synchronously crawls it server-side; an unreachable URL comes back
            // as a generic 500 with a plain-string detail rather than a field-level validation error.
            const message =
              isCreate && typeof (result as any)?.detail === "string"
                ? "Couldn't create the URL. Please verify it's reachable and try again."
                : getAskAidenErrorMessage(result, "Failed to save URL");
            setErrorMessage({ title: "Save Failed", message, visible: true });
            return;
          }

          reloadUrlsFromStart();
          handleCloseModal();
        } catch (error) {
          console.error("Failed to save AskAiden item", error);
          setErrorMessage({
            title: "Save Failed",
            message: (error as Error)?.message || "Failed to save AskAiden item",
            visible: true,
          });
        }
        return;
      }

      try {
        if (modalMode === "edit" && editingId) {
          const result = await api.documents.updateDocument(editingId, documentForm.name.trim(), documentForm.description.trim());

          if (isAskAidenApiError(result)) {
            setErrorMessage({ title: "Save Failed", message: getAskAidenErrorMessage(result, "Failed to update document"), visible: true });
            return;
          }
        } else {
          if (!documentForm.file) {
            console.warn("No file selected for upload");
            return;
          }
          if (documentForm.file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
            setErrorMessage({
              title: "Upload Failed",
              message: `File exceeds the maximum allowed size of ${MAX_UPLOAD_FILE_SIZE_MB} MB.`,
              visible: true,
            });
            return;
          }
          if (!isAllowedUploadFile(documentForm.file.name)) {
            setErrorMessage({
              title: "Upload Failed",
              message: `Unsupported file type. ${ALLOWED_UPLOAD_TYPES_NOTE}`,
              visible: true,
            });
            return;
          }

          const formData = new FormData();
          formData.append("file", documentForm.file);
          formData.append("description", documentForm.description.trim());
          // uploaded_by isn't sent - the server sets it from the validated admin's token.
          if (documentForm.type.trim()) {
            formData.append("folder_name", documentForm.type.trim());
          }
          const result = await api.documents.createDocument(formData);

          if (isAskAidenApiError(result)) {
            setFileError(getAskAidenErrorMessage(result, "Failed to upload document"));
            return;
          }
        }

        reloadDocumentsFromStart();
        handleCloseModal();
      } catch (error) {
        console.error("Failed to save AskAiden item", error);
        setErrorMessage({
          title: "Upload Failed",
          message: (error as Error)?.message || "Failed to save AskAiden item",
          visible: true,
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (id: number) => {
    setDeleteTargetId(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;

    try {
      const result =
        activeTab === "url-management"
          ? await api.documents.deleteUrl(deleteTargetId)
          : await api.documents.deleteDocument(deleteTargetId);

      const notFound = typeof (result as any)?.detail === "string" && /not found/i.test((result as any).detail);

      if (isAskAidenApiError(result) && !notFound) {
        setErrorMessage({ title: "Delete Failed", message: getAskAidenErrorMessage(result, "Failed to delete item"), visible: true });
        return;
      }

      // A 404 here means the row is already gone (deleted elsewhere) - the desired
      // end state already holds, so just refresh instead of showing an error.
      if (activeTab === "url-management") {
        reloadUrlsFromStart();
      } else {
        reloadDocumentsFromStart();
      }
    } catch (error) {
      console.error("Failed to delete AskAiden item", error);
      setErrorMessage({
        title: "Delete Failed",
        message: (error as Error)?.message || "Failed to delete AskAiden item",
        visible: true,
      });
    } finally {
      setDeleteTargetId(null);
      setDeleteConfirmOpen(false);
    }
  };

  const urlFieldError = useMemo(() => {
    const trimmed = urlForm.url.trim();
    if (!trimmed) return "";
    if (trimmed.length > URL_MAX_LENGTH) return `URL must be ${URL_MAX_LENGTH} characters or fewer`;
    return isValidHttpUrl(trimmed) ? "" : "Enter a valid URL (e.g. https://example.com)";
  }, [urlForm.url]);
  const isUrlValid = !!urlForm.url.trim() && urlForm.url.trim().length <= URL_MAX_LENGTH && isValidHttpUrl(urlForm.url.trim());

  const urlDescriptionError = useMemo(() => {
    const trimmed = urlForm.description.trim();
    if (trimmed.length > DESCRIPTION_MAX_LENGTH) return `Description must be ${DESCRIPTION_MAX_LENGTH} characters or fewer`;
    return "";
  }, [urlForm.description]);
  const isUrlDescriptionValid = !!urlForm.description.trim() && urlForm.description.trim().length <= DESCRIPTION_MAX_LENGTH;

  const documentDescriptionError = useMemo(() => {
    const trimmed = documentForm.description.trim();
    if (trimmed.length > DESCRIPTION_MAX_LENGTH) return `Description must be ${DESCRIPTION_MAX_LENGTH} characters or fewer`;
    return "";
  }, [documentForm.description]);
  const isDocumentDescriptionValid = !!documentForm.description.trim() && documentForm.description.trim().length <= DESCRIPTION_MAX_LENGTH;

  const documentNameError = useMemo(() => {
    const trimmed = documentForm.name.trim();
    if (trimmed.length > FILE_NAME_MAX_LENGTH) return `File name must be ${FILE_NAME_MAX_LENGTH} characters or fewer`;
    return "";
  }, [documentForm.name]);
  const isDocumentNameValid = !!documentForm.name.trim() && documentForm.name.trim().length <= FILE_NAME_MAX_LENGTH;

  // URL and document search/filtering both happen server-side (see loadUrls/loadDocuments) -
  // urlRows/documentRows are already exactly the current page's matching results.

  const urlColumns = useMemo<GridColDef[]>(
    () => [
      { field: "url", headerName: "URL", flex: 1 },
      { field: "description", headerName: "Description", flex: 1 },
      {
        field: "dateCreated",
        headerName: "Date Created",
        width: 180,
        renderCell: (params) => {
          if (!params.value) return "";

          const date = new Date(params.value);
          return isNaN(date.getTime()) ? params.value : format(date, "PPpp");
        },
      },
      {
        field: "dateModified",
        headerName: "Date Modified",
        width: 180,
        renderCell: (params) => {
          if (!params.value) return "";

          const date = new Date(params.value);
          return isNaN(date.getTime()) ? params.value : format(date, "PPpp");
        },
      },
      { field: "modifiedBy", headerName: "Modified By", width: 160 },
      {
        field: "actions",
        headerName: "Action",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box display="flex" gap={0.5} alignItems="center" height="100%">
            <Tooltip title={params.row.hasValidId ? "Edit" : "This item has no id from the server and can't be edited"} arrow>
              <span>
                <IconButton
                  size="small"
                  sx={{ color: "#8a8886" }}
                  disabled={!params.row.hasValidId}
                  onClick={() => handleOpenEditModal(params.row)}
                >
                  <EditRegular fontSize={22} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={params.row.hasValidId ? "Delete" : "This item has no id from the server and can't be deleted"} arrow>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  sx={{ color: "#d54c52" }}
                  disabled={!params.row.hasValidId}
                  onClick={() => handleDeleteClick(params.row.id)}
                >
                  <DeleteRegular fontSize={22} style={{ color: "#d5878b" }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [activeTab, userEmail]
  );

  const documentColumns = useMemo<GridColDef[]>(
    () => [
      { field: "name", headerName: "Name", flex: 1 },
      { field: "description", headerName: "Description", flex: 1 },
      {
        field: "type",
        headerName: "Type",
        width: 160,
        type: "singleSelect",
        valueOptions: DOCUMENT_TYPE_OPTIONS.map((option) => ({ value: option.key, label: option.label })),
        renderCell: (params) => getReadableFileType(params.row.type, params.row.name),
      },
      {
        field: "dateCreated",
        headerName: "Date Created",
        width: 180,
        renderCell: (params) => {
          if (!params.value) return "";

          const date = new Date(params.value);
          return isNaN(date.getTime()) ? params.value : format(date, "PPpp");
        },
      },
      {
        field: "dateModified",
        headerName: "Date Modified",
        width: 180,
        renderCell: (params) => {
          if (!params.value) return "";

          const date = new Date(params.value);
          return isNaN(date.getTime()) ? params.value : format(date, "PPpp");
        },
      },
      { field: "modifiedBy", headerName: "Modified By", width: 160 },
      {
        field: "actions",
        headerName: "Action",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box display="flex" gap={0.5} alignItems="center" height="100%">
            <Tooltip title={params.row.hasValidId ? "Edit" : "This item has no id from the server and can't be edited"} arrow>
              <span>
                <IconButton
                  size="small"
                  sx={{ color: "#8a8886" }}
                  disabled={!params.row.hasValidId}
                  onClick={() => handleOpenEditModal(params.row)}
                >
                  <EditRegular fontSize={22} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={params.row.hasValidId ? "Delete" : "This item has no id from the server and can't be deleted"} arrow>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  sx={{ color: "#d54c52" }}
                  disabled={!params.row.hasValidId}
                  onClick={() => handleDeleteClick(params.row.id)}
                >
                  <DeleteRegular fontSize={22} style={{ color: "#d5878b" }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [activeTab]
  );

  const dataGridStyles = useMemo(
    () => ({
      // MUI X v7 paints the column headers (and the filler/scrollbar-filler areas) from this
      // CSS variable, not a class-level background - overriding just ".MuiDataGrid-columnHeaders"
      // below is not enough, since its own background reads from this var at higher specificity.
      "--DataGrid-containerBackground": fluentUiDarkTheme.palette.blackTranslucent40,
      "& .MuiDataGrid-cell": {
        borderBottom: "1px solid var(--background-second)",
        backgroundColor: fluentUiDarkTheme.palette.blackTranslucent40,
        color: "#ffffff",
      },
      "& .MuiDataGrid-columnHeaders": {
        backgroundColor: fluentUiDarkTheme.palette.blackTranslucent40,
        color: fluentUiDarkTheme.palette.black,
      },
      "& .MuiDataGrid-filler": {
        backgroundColor: fluentUiDarkTheme.palette.blackTranslucent40,
      },
      "& .MuiDataGrid-footerContainer": {
        backgroundColor: fluentUiDarkTheme.palette.blackTranslucent40,
        color: "white",
        justifyContent: "center",
      },
      "& .MuiTablePagination-toolbar": {
        color: "white",
        alignItems: "baseline",
        paddingTop: "10px",
      },
    }),
    []
  );

  // The filter panel's "Columns/Operator/Value" labels float ~9px above their input by
  // default (standard MUI floating-label behavior), but the panel reserves no top padding
  // for that float - the label's top few px land outside the panel Paper's box and get cut
  // by its `overflow: auto`, with the grid's own opaque header showing through the gap since
  // the panel (a Popper portalled to body, not a descendant of the grid root) can't be
  // reached by the grid's own `sx` prop.
  const filterPanelSlotProps = useMemo(
    () => ({
      panel: {
        sx: {
          "& .MuiDataGrid-panelContent": {
            paddingTop: "20px",
          },
        },
      },
    }),
    []
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="alert-home">
        <div className="alert-title">
          <h2>AskAiden Documents</h2>
        </div>

        {isLoading ? (
          <LoadingPage />
        ) : (
          <TabContext value={activeTab}>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <TabList onChange={handleTabChange} aria-label="askaiden-documents-tabs">
                <Tab label="URL Management" value="url-management" sx={{ color: "white" }} />
                <Tab label="Document Management" value="document-management" sx={{ color: "white" }} />
              </TabList>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenAddModal}>
                  {activeTab === "url-management" ? "Add New URL" : "Upload Document"}
                </Button>
                <Button
                  className="refresh"
                  variant="outlined"
                  onClick={refreshHandler}
                  disabled={isUrlsLoading || isDocumentsLoading}
                >
                  Refresh
                  <div className="refresh-icon">
                    <Icon iconName="refresh" />
                  </div>
                </Button>
              </Box>
              <TextField
                size="small"
                placeholder="Search"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                sx={{ width: { xs: "100%", sm: 300 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ height: "640px", width: "100%" }}>
              {activeTab === "url-management" ? (
                <DataGrid
                  key="url-management"
                  rows={urlRows}
                  columns={urlColumns}
                  loading={isUrlsLoading}
                  paginationMode="server"
                  sortingMode="server"
                  filterMode="server"
                  sortModel={urlSortModel}
                  onSortModelChange={setUrlSortModel}
                  filterModel={urlFilterModel}
                  onFilterModelChange={setUrlFilterModel}
                  filterDebounceMs={500}
                  rowCount={urlTotalCount}
                  paginationModel={urlPaginationModel}
                  onPaginationModelChange={setUrlPaginationModel}
                  pageSizeOptions={[10, 20, 50, 100]}
                  disableRowSelectionOnClick
                  slotProps={filterPanelSlotProps}
                  sx={dataGridStyles}
                />
              ) : (
                <DataGrid
                  key="document-management"
                  rows={documentRows}
                  columns={documentColumns}
                  loading={isDocumentsLoading}
                  paginationMode="server"
                  sortingMode="server"
                  filterMode="server"
                  sortModel={documentSortModel}
                  onSortModelChange={setDocumentSortModel}
                  filterModel={documentFilterModel}
                  onFilterModelChange={setDocumentFilterModel}
                  filterDebounceMs={500}
                  rowCount={documentTotalCount}
                  paginationModel={documentPaginationModel}
                  onPaginationModelChange={setDocumentPaginationModel}
                  pageSizeOptions={[10, 20, 50, 100]}
                  disableRowSelectionOnClick
                  slotProps={filterPanelSlotProps}
                  sx={dataGridStyles}
                />
              )}
            </Box>
          </TabContext>
        )}

        <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: "#1b1a19", color: "#ffffff" }}>
          {modalMode === "add" ? (activeTab === "url-management" ? "Add URL" : "Upload Document") : (activeTab === "url-management" ? "Edit URL" : "Edit Document")}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#1b1a19", color: "#ffffff", display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {activeTab === "url-management" ? (
            <>
              <TextField
                label="URL"
                value={urlForm.url}
                onChange={(event) => setUrlForm((current) => ({ ...current, url: event.target.value }))}
                fullWidth
                size="small"
                error={!!urlFieldError}
                helperText={urlFieldError}
                sx={{ mt: "12px", input: { color: "#ffffff" }, label: { color: "#d7d7d7" } }}
              />
              <TextField
                label="Description"
                value={urlForm.description}
                onChange={(event) => setUrlForm((current) => ({ ...current, description: event.target.value }))}
                fullWidth
                size="small"
                multiline
                minRows={3}
                error={!!urlDescriptionError}
                helperText={urlDescriptionError}
                sx={{ textarea: { color: "#ffffff" }, label: { color: "#d7d7d7" } }}
              />
            </>
          ) : (
            <>
              {modalMode === "add" ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box
                    {...getRootProps()}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      p: 4,
                      border: "2px dashed rgba(255,255,255,0.4)",
                      borderRadius: 2,
                      minHeight: 220,
                      bgcolor: isDragActive ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.02)",
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    <input {...getInputProps()} />
                    <Typography sx={{ color: "#d7d7d7", fontSize: "1.1rem" }}>
                      Drag and drop or <Typography component="span" sx={{ color: "#02a6f2", textDecoration: "underline" }}>Browse</Typography>
                    </Typography>
                    <Typography sx={{ color: "#7f7f7f", fontSize: "0.95rem" }}>
                      Your file cannot be larger than {MAX_UPLOAD_FILE_SIZE_MB} MB.
                    </Typography>
                    <Typography sx={{ color: "#7f7f7f", fontSize: "0.8rem", mt: 0.5 }}>{ALLOWED_UPLOAD_TYPES_NOTE}</Typography>
                    {documentForm.fileName && (
                      <Typography sx={{ color: "#ffffff", fontSize: "0.95rem", mt: 1 }}>{documentForm.fileName}</Typography>
                    )}
                    {fileError && (
                      <Typography sx={{ color: "#f28b82", fontSize: "0.85rem", mt: 1 }}>{fileError}</Typography>
                    )}
                  </Box>

                  <TextField
                    label="Description"
                    value={documentForm.description}
                    onChange={(event) => setDocumentForm((current) => ({ ...current, description: event.target.value }))}
                    fullWidth
                    size="small"
                    multiline
                    minRows={3}
                    error={!!documentDescriptionError}
                    helperText={documentDescriptionError}
                    sx={{ textarea: { color: "#ffffff" }, label: { color: "#d7d7d7" } }}
                  />
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="New File Name"
                    value={documentForm.name}
                    onChange={(event) => setDocumentForm((current) => ({ ...current, name: event.target.value }))}
                    fullWidth
                    size="small"
                    error={!!documentNameError}
                    helperText={documentNameError}
                    sx={{ mt: "12px", input: { color: "#ffffff" }, label: { color: "#d7d7d7" } }}
                  />

                  <TextField
                    label="Description"
                    value={documentForm.description}
                    onChange={(event) => setDocumentForm((current) => ({ ...current, description: event.target.value }))}
                    fullWidth
                    size="small"
                    multiline
                    minRows={3}
                    error={!!documentDescriptionError}
                    helperText={documentDescriptionError}
                    sx={{ textarea: { color: "#ffffff" }, label: { color: "#d7d7d7" } }}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#1b1a19", px: 3, pb: 2 }}>
          <Button onClick={handleCloseModal} sx={{ color: "#ffffff" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={
              isSaving ||
              (activeTab === "url-management" && !(isUrlValid && isUrlDescriptionValid)) ||
              (activeTab === "document-management" &&
                modalMode === "add" &&
                (!documentForm.file || !isDocumentDescriptionValid)) ||
              (activeTab === "document-management" &&
                modalMode === "edit" &&
                !(isDocumentNameValid && isDocumentDescriptionValid))
            }
          >
            {isSaving ? "Saving..." : modalMode === "add" ? "Add New" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        title={activeTab === "url-management" ? "Delete URL" : "Delete Document"}
        subText="Are you sure you want to delete this item?"
        isVisible={deleteConfirmOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setDeleteTargetId(null);
        }}
      />

        <ErrorMessage
          title={errorMessage.title}
          message={errorMessage.message}
          isVisible={errorMessage.visible}
          onConfirm={() => setErrorMessage({ ...errorMessage, visible: false })}
        />
      </div>
    </ThemeProvider>
  );
};

export default AskAidenDocumentsPage;
